import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchAllMetrics, generatePeriodColumns } from '../services/weeklyMetrics';
import { clearAllCache } from '../services/cache';
import { metricDefinitions } from '../config/weeklyMetrics';
import type { MetricSection, ComputedMetric, PeriodType } from '../types/weeklyMetrics';
import type { AccountSegment } from '../types/segment';

interface UseKeyMetricsResult {
  sections: MetricSection[];
  periodColumns: string[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function groupBySection(metrics: ComputedMetric[]): MetricSection[] {
  const sectionMap = new Map<string, ComputedMetric[]>();

  for (const def of metricDefinitions) {
    if (!sectionMap.has(def.section)) {
      sectionMap.set(def.section, []);
    }
  }

  for (const metric of metrics) {
    const list = sectionMap.get(metric.definition.section);
    if (list) list.push(metric);
  }

  const sections: MetricSection[] = [];
  for (const [name, sectionMetrics] of sectionMap) {
    if (sectionMetrics.length === 0) continue;
    const primary = sectionMetrics.find((m) => m.definition.isPrimary);
    sections.push({
      name,
      metrics: sectionMetrics,
      primaryMetric: primary || sectionMetrics[0],
    });
  }

  return sections;
}

export function useKeyMetrics(periodType: PeriodType = 'weekly', segment: AccountSegment = 'all'): UseKeyMetricsResult {
  const [sections, setSections] = useState<MetricSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const periodColumns = generatePeriodColumns(periodType);

  const refetch = useCallback(() => {
    clearAllCache();
    setRefreshCounter((c) => c + 1);
  }, []);

  useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const metrics = await fetchAllMetrics(
          metricDefinitions,
          periodType,
          controller.signal,
          segment
        );
        if (!cancelled) {
          setSections(groupBySection(metrics));
        }
      } catch (err: unknown) {
        if (!cancelled && !(err instanceof DOMException && err.name === 'AbortError')) {
          setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [periodType, segment, refreshCounter]);

  return { sections, periodColumns, loading, error, refetch };
}
